(() => {
  if (window.HurcaProductPreview?.__hurcaGlobalPreview) {
    window.HurcaProductPreview.initialize?.();
    return;
  }

  let activeDialog = null;
  let activeOpen = null;
  let activeClose = null;
  let queuedSource = null;

  const initialize = () => {
    const dialog = document.querySelector('[data-hph-dialog]');
    if (!dialog) return false;
    if (activeDialog === dialog && activeOpen && activeClose) return true;
    if (dialog.dataset.hphInitialized === 'true') return false;
    dialog.dataset.hphInitialized = 'true';

    const image = dialog.querySelector('[data-hph-image]');
    const media = dialog.querySelector('[data-hph-media]');
    const title = dialog.querySelector('[data-hph-title]');
    const star = dialog.querySelector('[data-hph-star]');
    const description = dialog.querySelector('[data-hph-description]');
    const licenses = dialog.querySelector('[data-hph-licenses]');
    const status = dialog.querySelector('[data-hph-status]');
    const productLink = dialog.querySelector('[data-hph-product-link]');
    const storageKey = 'hurca-recently-viewed-v1';
    let previouslyFocusedElement = null;

    const labels = {
      add: dialog.dataset.addLabel || 'Add to cart',
      soldOut: dialog.dataset.soldOutLabel || 'Sold out',
      added: dialog.dataset.addedLabel || 'Added to cart',
      error: dialog.dataset.errorLabel || 'Unable to add this item.',
      standard: dialog.dataset.standardLabel || 'Standard licence',
      extended: dialog.dataset.extendedLabel || 'Extended licence'
    };

    const getSource = (button) => button?.closest(
      '[data-hurca-preview-product], [data-cghx-card], .tgh__item'
    );

    const recordRecentlyViewed = (source) => {
      if (!source) return;

      const productId = String(source.dataset.productId || source.dataset.tghProductId || '');
      const productHandle = String(source.dataset.productHandle || source.dataset.previewHandle || '');
      if (!productId && !productHandle) return;

      let history = [];
      try {
        const stored = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        history = Array.isArray(stored) ? stored : [];
      } catch (error) {
        history = [];
      }

      const withoutDuplicate = history.filter((item) => {
        if (!item) return false;
        const sameId = productId && String(item.id || '') === productId;
        const sameHandle = productHandle && String(item.handle || '') === productHandle;
        return !sameId && !sameHandle;
      });

      const updatedHistory = [
        {
          id: productId,
          handle: productHandle,
          url: source.dataset.previewUrl || source.dataset.productUrl || '',
          title: source.dataset.previewTitle || source.dataset.productTitle || source.dataset.title || '',
          image: source.dataset.previewImage || source.dataset.productImage || '',
          standardPrice: source.dataset.standardPrice || source.dataset.productPrice || '',
          visitedAt: Date.now()
        },
        ...withoutDuplicate
      ].slice(0, 100);

      try {
        window.localStorage.setItem(storageKey, JSON.stringify(updatedHistory));
      } catch (error) {
        return;
      }

      window.dispatchEvent(new CustomEvent('hurca:recently-viewed-updated', {
        detail: { items: updatedHistory, source: 'HurcaProductPreview' }
      }));
    };

    const createLicenseRow = (source, prefix) => {
      const variantId = source.dataset[`${prefix}Id`] || '';
      if (!variantId) return null;

      const available = source.dataset[`${prefix}Available`] === 'true';
      const row = document.createElement('div');
      row.className = 'hph__license';

      const name = document.createElement('span');
      name.className = 'hph__license-name';
      name.textContent = source.dataset[`${prefix}Title`] || labels[prefix] || '';

      const price = document.createElement('span');
      price.className = 'hph__license-price';
      price.textContent = source.dataset[`${prefix}Price`] || '';

      const addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'hph__cart-button';
      addButton.dataset.hphAdd = variantId;
      addButton.textContent = available ? labels.add : labels.soldOut;
      addButton.disabled = !available;

      row.append(name, price, addButton);
      return row;
    };

    const open = (source) => {
      if (!source || !licenses) return;

      recordRecentlyViewed(source);
      previouslyFocusedElement = document.activeElement;

      const imageUrl = source.dataset.previewImage || source.dataset.productImage || '';
      const imageAlt = source.dataset.previewImageAlt || source.dataset.previewTitle || source.dataset.productTitle || '';
      const productUrl = source.dataset.previewUrl || source.dataset.productUrl || '#';
      const productTitle = source.dataset.previewTitle || source.dataset.productTitle || source.dataset.title || '';
      const productDescription = source.dataset.previewDescription || '';

      if (image) {
        image.src = imageUrl;
        image.alt = imageAlt;
        image.hidden = !imageUrl;
      }
      if (media) media.hidden = !imageUrl;
      dialog.classList.toggle('hph--no-image', !imageUrl);
      if (title) title.textContent = productTitle;
      if (star) star.hidden = source.dataset.previewSignature !== 'true';
      if (description) {
        description.textContent = productDescription;
        description.hidden = !productDescription;
      }
      if (productLink) productLink.href = productUrl;

      licenses.replaceChildren();
      ['standard', 'extended'].forEach((prefix) => {
        const row = createLicenseRow(source, prefix);
        if (row) licenses.appendChild(row);
      });
      if (status) status.textContent = '';

      document.documentElement.classList.add('hph-is-open');
      document.body.classList.add('hph-is-open');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    };

    const close = () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };

    const addVariant = async (button) => {
      const variantId = String(button.dataset.hphAdd || '');
      if (!variantId) return;

      const defaultLabel = button.textContent;
      button.disabled = true;
      if (status) status.textContent = '';

      try {
        if (window.Shopify?.actions?.updateCart) {
          const result = await window.Shopify.actions.updateCart({
            lines: [{ merchandiseId: variantId, quantity: 1 }]
          }, {
            event: { context: 'dialog', detail: { source: 'product-preview-hurca' } }
          });
          if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
        } else {
          const localeRoot = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${localeRoot}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
          });
          const responseData = await response.json();
          if (!response.ok || responseData?.status) {
            throw new Error(responseData?.description || responseData?.message || labels.error);
          }
          window.dispatchEvent(new CustomEvent('hurca:cart-updated', { detail: responseData }));
        }

        button.textContent = labels.added;
        if (status) status.textContent = labels.added;
      } catch (error) {
        if (status) status.textContent = error?.message || labels.error;
      } finally {
        window.setTimeout(() => {
          button.textContent = defaultLabel;
          button.disabled = false;
        }, 1600);
      }
    };

    document.addEventListener('click', (event) => {
      const addButton = event.target.closest('[data-hph-add]');
      if (addButton) {
        event.preventDefault();
        addVariant(addButton);
        return;
      }

      const closeButton = event.target.closest('[data-hph-close]');
      if (closeButton) {
        event.preventDefault();
        close();
        return;
      }

      if (event.defaultPrevented) return;
      const previewButton = event.target.closest('[data-hurca-preview], [data-cghx-preview], [data-tgh-preview]');
      if (!previewButton) return;

      const source = getSource(previewButton);
      if (!source) return;
      event.preventDefault();
      open(source);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('hph-is-open');
      document.body.classList.remove('hph-is-open');
      previouslyFocusedElement?.focus?.();
      previouslyFocusedElement = null;
    });

    window.addEventListener('hurca:product-preview:open', (event) => {
      if (event.detail?.source) open(event.detail.source);
    });

    activeDialog = dialog;
    activeOpen = open;
    activeClose = close;

    if (queuedSource) {
      const source = queuedSource;
      queuedSource = null;
      window.requestAnimationFrame(() => activeOpen?.(source));
    }

    return true;
  };

  const publicApi = {
    __hurcaGlobalPreview: true,
    initialize,
    open(source) {
      if (!activeDialog?.isConnected) {
        activeDialog = null;
        activeOpen = null;
        activeClose = null;
      }

      if (!activeOpen) initialize();
      if (activeOpen) activeOpen(source);
      else if (source) queuedSource = source;
    },
    close() {
      if (!activeClose) initialize();
      activeClose?.();
    }
  };

  window.HurcaProductPreview = publicApi;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();